import { useTranslation } from 'react-i18next';
import {
  ACCOUNT_LEGAL_LINK_DEFS,
  FOOTER_LEGAL_LINK_DEFS,
  type LegalLinkItem,
  type LegalLinkKey,
} from '@jwp/ott-common/src/constants/legalLinks';
import { useAppInfo } from './useAppInfo';

type MultilingualURL = { en?: string; es?: string } | undefined;

const resolveLocalizedUrl = (doc: MultilingualURL, language: string): string => {
  if (!doc) return '';
  const lang = language?.startsWith('es') ? 'es' : 'en';
  return (lang === 'es' ? doc.es || doc.en : doc.en || doc.es) || '';
};

/**
 * Resolves legal document links from Firebase `common.legal_docs`.
 * URLs are language-aware (en/es) with fallback to the other language when empty.
 */
export const useLegalLinks = () => {
  const { i18n } = useTranslation();
  const { termsOfUse, privacyPolicy, videoConsentViewingPolicy, dmcaNotice, doNotSellOrShare } = useAppInfo();

  const urlByKey: Record<LegalLinkKey, string> = {
    termsOfUse: resolveLocalizedUrl(termsOfUse, i18n.language),
    privacyPolicy: resolveLocalizedUrl(privacyPolicy, i18n.language),
    videoConsentViewingPolicy: resolveLocalizedUrl(videoConsentViewingPolicy, i18n.language),
    dmcaNotice: resolveLocalizedUrl(dmcaNotice, i18n.language),
    doNotSellOrShare: resolveLocalizedUrl(doNotSellOrShare, i18n.language),
  };

  const withUrls = (defs: Omit<LegalLinkItem, 'url'>[]): LegalLinkItem[] =>
    defs.map((def) => ({
      ...def,
      url: urlByKey[def.key] || undefined,
    }));

  const footerLegalLinks = withUrls(FOOTER_LEGAL_LINK_DEFS).filter((link) => Boolean(link.url));
  const accountLegalLinks = withUrls(ACCOUNT_LEGAL_LINK_DEFS).filter((link) => Boolean(link.url));

  return {
    footerLegalLinks,
    accountLegalLinks,
    termsOfUseUrl: urlByKey.termsOfUse,
    privacyPolicyUrl: urlByKey.privacyPolicy,
    videoConsentViewingPolicyUrl: urlByKey.videoConsentViewingPolicy,
    dmcaNoticeUrl: urlByKey.dmcaNotice,
    doNotSellOrShareUrl: urlByKey.doNotSellOrShare,
  };
};
